import React, { useState, useEffect } from 'react';
import { Edit, Add, CheckCircle, Error as ErrorIcon, Schedule, RadioButtonUnchecked } from '@mui/icons-material';
import { getContracts, createContract, updateContract, toggleContractFetch, ContractAdminDto, CreateContractDto } from '../../services/contractsService';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import AddFromRefinitivWizard from './refinitiv/AddFromRefinitivWizard';
import ContractRefinitivActions from './refinitiv/ContractRefinitivActions';
import { useWorkerStatus } from './refinitiv/useRefinitivJob';

const CONTRACT_TYPES = [
  { value: 'material', label: 'Material' },
  { value: 'fx', label: 'FX' },
  { value: 'freight', label: 'Freight' },
  { value: 'index', label: 'Index' },
  { value: 'synthetic', label: 'Synthetic' },
];

const PROVIDERS = [
  { value: 'refinitiv', label: 'Refinitiv' },
  { value: 'bloomberg', label: 'Bloomberg' },
  { value: 'manual', label: 'Manual' },
];

const RIC_METHODS = [
  { value: 'monthly', label: 'Monthly futures', hint: 'One contract per traded month, e.g. SBH6, SBK6 (base + month code + year)' },
  { value: 'terminal_groups', label: 'Terminal groups', hint: 'Grouped delivery months without year, e.g. RFVCBLEUDFGH (cocoa physicals)' },
  { value: 'continuation', label: 'Continuation series', hint: 'Rolling front contracts, e.g. CLc1..CLc12' },
  { value: 'spot_only', label: 'Spot only', hint: 'Single quote, no curve — FX, indices, assessments' },
];

const MONTH_CODES = ['F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'ILS', 'CNY', 'JPY'];

const QUOTE_UNITS = [
  { value: 'metricTon', label: 'Metric Ton (MT)' },
  { value: 'shortTon', label: 'Short Ton' },
  { value: 'grossTon', label: 'Gross Ton' },
  { value: 'cwt', label: 'Hundredweight (cwt)' },
  { value: 'lbs', label: 'Pounds (lbs)' },
  { value: 'libra', label: 'Libra' },
  { value: 'bbl', label: 'Barrel (bbl)' },
  { value: 'index', label: 'Index Points' },
  { value: 'rate', label: 'Rate (FX)' },
];

/**
 * Derive the RIC stem from a quote symbol by stripping a Refinitiv
 * continuation suffix (e.g. FAPPc1 → FAPP, CLc1 → CL).
 */
function deriveBaseSymbol(symbol: string | undefined): string | undefined {
  return symbol?.replace(/c\d+$/, '');
}

/** Include a legacy/unknown value in a dropdown so editing old rows doesn't lose it */
function withCurrentOption<T extends string>(options: T[], current: string | undefined): string[] {
  if (current && !options.includes(current as T)) return [current, ...options];
  return options;
}

const ContractsManagement: React.FC = () => {
  useCardBar({ visible: false });

  const [contracts, setContracts] = useState<ContractAdminDto[]>([]);
  const [filteredContracts, setFilteredContracts] = useState<ContractAdminDto[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [fetchEnabledFilter, setFetchEnabledFilter] = useState<string>('all');
  
  const [sortColumn, setSortColumn] = useState<string>('code');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [showDrawer, setShowDrawer] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractAdminDto | null>(null);
  const [formData, setFormData] = useState<Partial<ContractAdminDto>>({
    type: 'material',
    currency: 'USD',
    quoteUnit: 'metricTon',
    provider: 'refinitiv',
    ricMethod: 'monthly',
    ricYearStyle: '2_digit',
    spotSuffix: 'c1',
    tradingCalendar: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    futuresDepth: 12,
    fetchEnabled: true,
    active: true,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const worker = useWorkerStatus();

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [contracts, searchQuery, typeFilter, statusFilter, fetchEnabledFilter, sortColumn, sortDirection]);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const data = await getContracts();
      setContracts(data);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const applyFilters = () => {
    let filtered = [...contracts];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        c => c.code.toLowerCase().includes(query) ||
             c.name.toLowerCase().includes(query) ||
             c.symbol.toLowerCase().includes(query)
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.lastFetchStatus === statusFilter);
    }

    if (fetchEnabledFilter !== 'all') {
      filtered = filtered.filter(c => 
        fetchEnabledFilter === 'enabled' ? c.active : !c.active
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal: any = a[sortColumn as keyof ContractAdminDto];
      let bVal: any = b[sortColumn as keyof ContractAdminDto];
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Convert to strings for comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredContracts(filtered);
  };

  const enabledContracts = filteredContracts.filter(c => c.active);
  const disabledContracts = filteredContracts.filter(c => !c.active);

  const handleCreateNew = () => {
    setEditingContract(null);
    setFormData({
      type: 'material',
      currency: 'USD',
      quoteUnit: 'metricTon',
      provider: 'refinitiv',
      ricMethod: 'monthly',
      ricYearStyle: '2_digit',
      spotSuffix: 'c1',
      tradingCalendar: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
      futuresDepth: 12,
      fetchEnabled: true,
      active: true,
    });
    setShowAdvanced(false);
    setShowDrawer(true);
  };

  const toggleCalendarMonth = (monthIdx: number) => {
    const current = formData.tradingCalendar || [];
    const next = current.includes(monthIdx)
      ? current.filter(m => m !== monthIdx)
      : [...current, monthIdx].sort((a, b) => a - b);
    setFormData({ ...formData, tradingCalendar: next });
  };

  const handleEdit = (contract: ContractAdminDto) => {
    setEditingContract(contract);
    setFormData({
      ...contract,
      ricMethod: contract.ricMethod || 'monthly',
      baseSymbol: contract.baseSymbol || deriveBaseSymbol(contract.symbol),
    });
    setShowAdvanced(false);
    setShowDrawer(true);
  };

  const handleSave = async () => {
    const method = formData.ricMethod || 'monthly';
    const needsCalendar = method === 'monthly' || method === 'terminal_groups';
    if (needsCalendar && (!formData.tradingCalendar || formData.tradingCalendar.length === 0)) {
      alert('Select at least one month in the trading calendar');
      return;
    }
    const payload: Partial<CreateContractDto> = {
      ...formData,
      ricMethod: method,
      baseSymbol: formData.baseSymbol || deriveBaseSymbol(formData.symbol),
      // Only send fields relevant for the chosen method; clear the rest
      ricYearStyle: method === 'monthly' ? (formData.ricYearStyle || '2_digit') : null,
      tradingCalendar: needsCalendar ? formData.tradingCalendar : null,
      spotSuffix: method === 'monthly' || method === 'terminal_groups' ? (formData.spotSuffix || 'c1') : null,
      futuresDepth: method === 'spot_only' ? undefined : (formData.futuresDepth ?? 12),
    };
    try {
      if (editingContract) {
        await updateContract(editingContract.id, payload);
      } else {
        await createContract(payload as CreateContractDto);
      }
      setShowDrawer(false);
      await loadContracts();
    } catch (error) {
      console.error('Failed to save contract:', error);
      alert(`Failed to save contract: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleToggleFetch = async (contractId: string, currentValue: boolean) => {
    try {
      await toggleContractFetch(contractId, !currentValue);
      
      setContracts(prev => prev.map(c => 
        c.id === contractId ? { ...c, active: !currentValue } : c
      ));
    } catch (error) {
      console.error('Failed to toggle fetch:', error);
      alert(`Failed to toggle fetch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getFetchStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
            <CheckCircle fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
            Success
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
            <ErrorIcon fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
            Failed
          </span>
        );
      case 'skipped':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
            <Schedule fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
            Skipped
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
            <Schedule fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
            In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#f3f4f6', color: '#374151' }}>
            <RadioButtonUnchecked fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
            Never
          </span>
        );
    }
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '–';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="flex flex-col h-full p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 text-left">Contracts Management</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage market contracts, quote sources, and price fetching
        </p>
        {worker && (
          <p className="text-xs mt-1 text-left">
            Refinitiv worker (VM):{' '}
            {worker.online
              ? <span className="text-green-700">online</span>
              : <span className="text-amber-700">offline{worker.lastSeenAt ? ` — last seen ${new Date(worker.lastSeenAt).toLocaleString()}` : ' — never connected'}</span>}
            {worker.queued > 0 && <span className="text-gray-500"> · {worker.queued} job(s) waiting</span>}
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <input
            type="text"
            placeholder="Search code, name, symbol..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            {CONTRACT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="in_progress">In Progress</option>
            <option value="never">Never Fetched</option>
          </select>

          <select
            value={fetchEnabledFilter}
            onChange={(e) => setFetchEnabledFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Fetch: All</option>
            <option value="enabled">Fetch: Enabled</option>
            <option value="disabled">Fetch: Disabled</option>
          </select>

          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
          >
            <Add fontSize="small" className="mr-1" />
            New Contract
          </button>

          <button
            onClick={() => setShowWizard(true)}
            disabled={!worker}
            title={worker ? 'Look the commodity up on Refinitiv and fill the settings automatically' : 'Superadmin only'}
            className="px-4 py-2 rounded-lg text-white flex items-center justify-center disabled:opacity-50"
            style={{ backgroundColor: '#9333ea' }}
          >
            <Add fontSize="small" className="mr-1" />
            Add from Refinitiv
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th onClick={() => handleSort('code')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Code {sortColumn === 'code' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('name')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('type')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Type {sortColumn === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('provider')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Quote Source {sortColumn === 'provider' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fetch</th>
              <th onClick={() => handleSort('lastFetchStatus')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Status {sortColumn === 'lastFetchStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('lastFetchAt')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Last Fetch {sortColumn === 'lastFetchAt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('firstPriceDate')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                First Price {sortColumn === 'firstPriceDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('latestPrice')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                Latest Spot Price {sortColumn === 'latestPrice' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  Loading contracts...
                </td>
              </tr>
            ) : filteredContracts.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  No contracts found
                </td>
              </tr>
            ) : (
              [...enabledContracts, ...(enabledContracts.length > 0 && disabledContracts.length > 0 ? [null] : []), ...disabledContracts].map((contract) => {
                if (contract === null) {
                  return (
                    <tr key="__sep__">
                      <td colSpan={10} className="px-4 py-1.5 bg-gray-100">
                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Disabled ({disabledContracts.length})</span>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={contract.id} className={`hover:bg-gray-50 ${!contract.active ? 'opacity-40' : ''}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{contract.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{contract.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{contract.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <span className="font-medium">{contract.provider}</span> • {contract.symbol}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleFetch(contract.id, contract.active)}
                        className="relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none z-0"
                        style={{ backgroundColor: contract.active ? '#22c55e' : '#d1d5db' }}
                      >
                        <span
                          className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                          style={{ transform: contract.active ? 'translateX(22px)' : 'translateX(2px)' }}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">{getFetchStatusBadge(contract.lastFetchStatus)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDateTime(contract.lastFetchAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {contract.firstPriceDate ? contract.firstPriceDate.split('T')[0] : <span className="text-gray-400">–</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {contract.latestPrice ? (
                        <div>
                          <div className="font-medium">{Number(contract.latestPrice).toFixed(2)} {contract.currency}</div>
                          <div className="text-xs text-gray-500">{contract.latestPriceDate?.split('T')[0]}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleEdit(contract)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit contract"
                      >
                        <Edit fontSize="small" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Edit/Create Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
          <div className="bg-white h-full w-full max-w-2xl overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {editingContract ? 'Edit Contract' : 'New Contract'}
              </h2>
              <button
                onClick={() => setShowDrawer(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                    <input
                      type="text"
                      value={formData.code || ''}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., BUS_US_MW"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Busheling Scrap US Midwest"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={formData.type || 'material'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {CONTRACT_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                    <select
                      value={formData.currency || 'USD'}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {withCurrentOption(CURRENCIES, formData.currency).map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quote Unit *</label>
                    <select
                      value={formData.quoteUnit || 'metricTon'}
                      onChange={(e) => setFormData({ ...formData, quoteUnit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {withCurrentOption(QUOTE_UNITS.map(u => u.value), formData.quoteUnit).map(v => (
                        <option key={v} value={v}>{QUOTE_UNITS.find(u => u.value === v)?.label || v}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Unit the exchange quotes the price in</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                    <input
                      type="text"
                      value={formData.region || ''}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., US Midwest, Turkey"
                    />
                  </div>
                </div>
              </div>

              {/* Quote Source Config */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Quote Source Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
                    <select
                      value={formData.provider || 'refinitiv'}
                      onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {PROVIDERS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Symbol *</label>
                    <input
                      type="text"
                      value={formData.symbol || ''}
                      onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., BUS, LCO, EUR="
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.active || false}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable automatic fetching</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* RIC Construction */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">RIC Construction</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">RIC Method *</label>
                    <select
                      value={formData.ricMethod || 'monthly'}
                      onChange={(e) => setFormData({ ...formData, ricMethod: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {RIC_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {RIC_METHODS.find(m => m.value === (formData.ricMethod || 'monthly'))?.hint}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Symbol (RIC stem) *</label>
                    <input
                      type="text"
                      value={formData.baseSymbol ?? deriveBaseSymbol(formData.symbol) ?? ''}
                      onChange={(e) => setFormData({ ...formData, baseSymbol: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., SB, CL, RFVCBLEUD"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      RIC stem without month/year/continuation suffix (for CLc1 the base is CL)
                    </p>
                    {/c\d+$/.test(formData.baseSymbol || '') && formData.ricMethod !== 'spot_only' && (
                      <p className="text-xs text-amber-600 mt-1 font-medium">
                        ⚠ Base symbol ends with a continuation suffix ({formData.baseSymbol?.match(/c\d+$/)?.[0]}).
                        This is usually wrong — generated RICs would be e.g. &quot;{formData.baseSymbol}X5&quot;.
                        Keep it only if the RIC stem genuinely ends this way.
                      </p>
                    )}
                  </div>

                  {(formData.ricMethod === 'monthly' || formData.ricMethod === 'terminal_groups') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.ricMethod === 'monthly' ? 'Traded Months *' : 'Terminal Group Boundaries * (last month of each group)'}
                      </label>
                      <div className="grid grid-cols-6 gap-1">
                        {MONTH_NAMES.map((name, idx) => {
                          const selected = (formData.tradingCalendar || []).includes(idx);
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => toggleCalendarMonth(idx)}
                              className={`px-1 py-1.5 text-xs rounded border text-center ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                            >
                              {name} <span className="opacity-60">({MONTH_CODES[idx]})</span>
                            </button>
                          );
                        })}
                      </div>
                      {formData.ricMethod === 'terminal_groups' && (
                        <p className="text-xs text-gray-500 mt-1">
                          e.g. Mar,May,Jul,Sep,Dec → groups FGH / JK / MN / QU / VXZ
                        </p>
                      )}
                    </div>
                  )}

                  {formData.ricMethod === 'monthly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year Style *</label>
                      <div className="flex space-x-2">
                        {(['1_digit', '2_digit'] as const).map(style => (
                          <button
                            key={style}
                            type="button"
                            onClick={() => setFormData({ ...formData, ricYearStyle: style })}
                            className={`px-3 py-1.5 text-sm rounded border ${(formData.ricYearStyle || '2_digit') === style ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
                          >
                            {style === '1_digit' ? '1-digit (SBH6)' : '2-digit (TIOH26)'}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Exchange convention — check the RIC in Workspace</p>
                    </div>
                  )}

                  <div>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {showAdvanced ? '▾ Hide advanced' : '▸ Advanced (spot suffix, depth, unit conversion)'}
                      </button>
                      {showAdvanced && (
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Multiplier</label>
                            <input
                              type="number"
                              step="any"
                              value={formData.unitMultiplier ?? 1}
                              onChange={(e) => setFormData({ ...formData, unitMultiplier: parseFloat(e.target.value) || 1 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Applied to vendor quotes at ingestion so stored prices are per {formData.quoteUnit || 'MT'} (e.g. 10 for EUR/100kg)
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Source Unit</label>
                            <input
                              type="text"
                              value={formData.sourceUnit ?? ''}
                              onChange={(e) => setFormData({ ...formData, sourceUnit: e.target.value || null })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g. EUR/100kg (leave empty if same as quote unit)"
                            />
                            <p className="text-xs text-gray-500 mt-1">Unit the exchange actually quotes in, if different</p>
                          </div>
                          {(formData.unitMultiplier ?? 1) !== 1 && (
                            <p className="col-span-2 text-xs text-amber-600 font-medium">
                              ⚠ Vendor quotes will be multiplied by {formData.unitMultiplier} before storage. Raw quotes are kept in raw_price.
                            </p>
                          )}
                          {formData.ricMethod !== 'continuation' && formData.ricMethod !== 'spot_only' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Spot Suffix</label>
                              <input
                                type="text"
                                value={formData.spotSuffix ?? 'c1'}
                                onChange={(e) => setFormData({ ...formData, spotSuffix: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="c1"
                              />
                              <p className="text-xs text-gray-500 mt-1">RIC used for the spot price (base + suffix, e.g. SBc1)</p>
                            </div>
                          )}
                          {formData.ricMethod !== 'spot_only' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Futures Depth</label>
                              <input
                                type="number"
                                value={formData.futuresDepth ?? 12}
                                onChange={(e) => setFormData({ ...formData, futuresDepth: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                min="1"
                                max="24"
                              />
                              <p className="text-xs text-gray-500 mt-1">How many months/contracts forward (default 12)</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                </div>
              </div>

              {/* Fetch Status (read-only for edit) */}
              {editingContract && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Fetch Status</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      {getFetchStatusBadge(editingContract.lastFetchStatus)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Last Fetch:</span>
                      <span className="text-sm text-gray-900">{formatDateTime(editingContract.lastFetchAt)}</span>
                    </div>
                    {editingContract.lastFetchError && (
                      <div>
                        <span className="text-sm text-gray-600">
                          {editingContract.lastFetchStatus === 'success' ? 'Message:' : 'Error:'}
                        </span>
                        <p className={`text-sm mt-1 ${editingContract.lastFetchStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                          {editingContract.lastFetchError}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {editingContract && worker && editingContract.provider === 'refinitiv' && (
                <ContractRefinitivActions
                  contractId={editingContract.id}
                  fetchEnabled={editingContract.active}
                  workerOnline={worker.online}
                  onDataChanged={loadContracts}
                />
              )}
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowDrawer(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#9333ea' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7e22ce'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9333ea'}
              >
                {editingContract ? 'Update Contract' : 'Create Contract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showWizard && (
        <AddFromRefinitivWizard
          existingCodes={contracts.map((c) => c.code)}
          workerOnline={worker?.online}
          llm={worker?.llm}
          onClose={() => setShowWizard(false)}
          onCreated={loadContracts}
        />
      )}
    </div>
  );
};

export default ContractsManagement;
